"use client";

import { useMemo, useState } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { PublicSubscriptionPlan } from "@/shared/services/publicPlansApi";
import { startHref, trackMarketing } from "./launch";
import s from "./MarketingPricing.module.scss";

function yearlyPerMonth(plan: PublicSubscriptionPlan): number | null {
    if (typeof plan.yearlyAmount === "number" && plan.yearlyAmount > 0) {
        return Math.round(plan.yearlyAmount / 12);
    }
    if (typeof plan.amount === "number" && Number.isFinite(plan.amount)) {
        return Math.round(plan.amount * 0.8);
    }
    return null;
}

function pickPlan(plans: PublicSubscriptionPlan[], code: string): PublicSubscriptionPlan | undefined {
    return plans.find((p) => p.code === code);
}

const DEFAULT_BUSINESS: PublicSubscriptionPlan = {
    code: "business",
    name: "Business",
    audienceLabel: "",
    amount: 99,
    yearlyAmount: 948,
    currency: "BYN",
    badge: "Hit",
    featured: true,
    bullets: [],
};

const DEFAULT_PRO: PublicSubscriptionPlan = {
    code: "pro",
    name: "Pro",
    audienceLabel: "",
    amount: 189,
    yearlyAmount: 1812,
    currency: "BYN",
    bullets: [],
};

export function MarketingPricingPage({
    plans,
    embed = false,
    showCompare,
    sectionId,
}: {
    plans: PublicSubscriptionPlan[];
    /** Встройка на главной / решениях — без отдельного H1 страницы тарифов. */
    embed?: boolean;
    showCompare?: boolean;
    sectionId?: string;
}) {
    const { t, i18n } = useTranslation("common");
    const m = "home.tarify.marketing";
    const [billing, setBilling] = useState<"month" | "year">("month");
    const compare = showCompare ?? !embed;
    const numberLocale = i18n.language?.startsWith("en") ? "en-US" : "ru-BY";

    const business = pickPlan(plans, "business") || DEFAULT_BUSINESS;
    const pro = pickPlan(plans, "pro") || DEFAULT_PRO;
    const cards = useMemo(() => [business, pro], [business, pro]);
    const alfaBullets = t(`${m}.alfaBullets`, { returnObjects: true }) as string[];
    const clarify = t(`${m}.compareRows.clarify`);

    const planTitle = (plan: PublicSubscriptionPlan) => {
        if (plan.code === "business") return t(`${m}.planBusiness`, { defaultValue: "Business" });
        if (plan.code === "pro" || plan.name === "Про / СКУД" || plan.name === "Про") {
            return t(`${m}.planPro`, { defaultValue: "Pro" });
        }
        return plan.name;
    };

    const TitleTag = embed ? "h2" : "h1";

    return (
        <div className={`${s.root} ${embed ? s.embed : ""}`} id={sectionId || (embed ? "tarify" : undefined)}>
            <section className={s.intro}>
                <span className={s.eyebrow}>{t(`${m}.eyebrow`)}</span>
                <TitleTag>
                    {t(`${m}.titleLine1`)}
                    <br />
                    {t(`${m}.titleLine2`)}
                </TitleTag>
                <p>{t(`${m}.intro`)}</p>

                <div className={s.toggle} role="group" aria-label={t(`${m}.billingGroupAria`)}>
                    <button
                        type="button"
                        className={billing === "month" ? s.toggleOn : undefined}
                        onClick={() => setBilling("month")}
                    >
                        {t(`${m}.billingMonth`)}
                    </button>
                    <button
                        type="button"
                        className={billing === "year" ? s.toggleOn : undefined}
                        onClick={() => setBilling("year")}
                    >
                        {t(`${m}.billingYear`)} <span>{t(`${m}.yearDiscount`)}</span>
                    </button>
                </div>
            </section>

            <section className={s.grid} aria-label={t(`${m}.gridAria`)}>
                {cards.map((plan) => {
                    const monthPrice = typeof plan.amount === "number" ? plan.amount : null;
                    const yearMonth = yearlyPerMonth(plan);
                    const showYear = billing === "year" && yearMonth != null;
                    const price = showYear ? yearMonth : monthPrice;
                    const featured = plan.code === "business";
                    const localeBulletsRaw = t(
                        plan.code === "pro" ? `${m}.proBullets` : `${m}.businessBullets`,
                        { returnObjects: true },
                    );
                    const localeBullets = Array.isArray(localeBulletsRaw)
                        ? (localeBulletsRaw as string[]).filter((x) => typeof x === "string" && x.trim())
                        : [];
                    const apiBullets = (plan.bullets?.length ? plan.bullets : plan.features || []).slice(0, 6);
                    // Prefer locale copy whenever available so EN is not overridden by RU API plan text.
                    const bullets = localeBullets.length ? localeBullets : apiBullets;
                    const localeAudience = t(
                        plan.code === "pro" ? `${m}.proAudience` : `${m}.businessAudience`,
                    );
                    const audience =
                        (localeAudience && !localeAudience.startsWith(m) ? localeAudience : "") ||
                        plan.audienceLabel ||
                        plan.description;

                    return (
                        <article key={plan.code} className={`${s.card} ${featured ? s.cardFeatured : ""}`}>
                            {featured ? <span className={s.hit}>{t(`${m}.hitFeatured`)}</span> : null}
                            <h2>{planTitle(plan)}</h2>
                            <p className={s.audience}>{audience}</p>
                            <div className={s.price}>
                                {price != null ? (
                                    <>
                                        {showYear && monthPrice != null ? (
                                            <span className={s.was}>
                                                {new Intl.NumberFormat(numberLocale).format(monthPrice)}{" "}
                                                {plan.currency || "BYN"}
                                            </span>
                                        ) : null}
                                        <strong>{new Intl.NumberFormat(numberLocale).format(price)}</strong>
                                        <span>
                                            {showYear ? t(`${m}.perMonthYearly`) : t(`${m}.perMonth`)}
                                        </span>
                                    </>
                                ) : (
                                    <span>{t(`${m}.priceOnRequest`)}</span>
                                )}
                            </div>
                            <a
                                className={s.cta}
                                href={startHref(undefined, plan.code)}
                                style={{ color: "#fff", WebkitTextFillColor: "#fff" }}
                                onClick={() =>
                                    trackMarketing("pricing_cta", {
                                        plan: plan.code,
                                        billing,
                                        source: embed ? "home_tariffs" : "tariffs_page",
                                    })
                                }
                            >
                                {t(`${m}.ctaTryFree`)}
                            </a>
                            <p className={s.trial}>{t(`${m}.trialHint`)}</p>
                            <ul>
                                {bullets.map((x) => (
                                    <li key={x}>
                                        <Check size={16} aria-hidden />
                                        <span>{x}</span>
                                    </li>
                                ))}
                            </ul>
                        </article>
                    );
                })}

                <article className={`${s.card} ${s.cardAlfa}`}>
                    <span className={s.hit}>{t(`${m}.hitSpecial`)}</span>
                    <h2>{t(`${m}.alfaTitle`)}</h2>
                    <p className={s.audience}>{t(`${m}.alfaSubtitle`)}</p>
                    <div className={s.price}>
                        <strong>50</strong>
                        <span>{t(`${m}.perMonth`)}</span>
                    </div>
                    <a
                        className={s.alfaCta}
                        href={startHref(undefined, "pro_alfa", undefined, "ALFA")}
                        style={{ color: "#c81e1e", WebkitTextFillColor: "#c81e1e" }}
                        onClick={() =>
                            trackMarketing("pricing_alfa_cta", {
                                source: embed ? "home_tariffs" : "tariffs_page",
                            })
                        }
                    >
                        {t(`${m}.alfaConnect`)}
                        <ArrowUpRight size={17} aria-hidden color="#c81e1e" />
                    </a>
                    <p className={s.trial}>{t(`${m}.alfaTrialHint`)}</p>
                    <ul>
                        {(Array.isArray(alfaBullets) ? alfaBullets : []).map((x) => (
                            <li key={x}>
                                <Check size={16} aria-hidden />
                                <span>{x}</span>
                            </li>
                        ))}
                    </ul>
                </article>
            </section>

            {compare ? (
                <section className={s.compare} id="comparison">
                    <h2>{t(`${m}.compareTitle`)}</h2>
                    <div className={s.tableScroll} role="region" aria-label={t(`${m}.compareAria`)} tabIndex={0}>
                        <table>
                            <thead>
                                <tr>
                                    <th scope="col">{t(`${m}.compareFeatureCol`)}</th>
                                    <th scope="col">{t(`${m}.planBusiness`, { defaultValue: "Business" })}</th>
                                    <th scope="col">{t(`${m}.planPro`, { defaultValue: "Pro" })}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(
                                    [
                                        [t(`${m}.compareRows.products`), business.maxProducts ?? 300, pro.maxProducts ?? clarify],
                                        [t(`${m}.compareRows.services`), business.maxServices ?? 300, pro.maxServices ?? clarify],
                                        [t(`${m}.compareRows.staff`), business.maxStaff ?? 25, pro.maxStaff ?? 80],
                                        [t(`${m}.compareRows.locations`), business.maxLocations ?? 1, pro.maxLocations ?? 1],
                                        [t(`${m}.compareRows.accessBooking`), "—", "✓"],
                                        [t(`${m}.compareRows.horecaKds`), "—", "✓"],
                                    ] as const
                                ).map(([label, b, p]) => (
                                    <tr key={String(label)}>
                                        <th scope="row">{label}</th>
                                        <td>{b}</td>
                                        <td>{p}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className={s.micro}>{t(`${m}.compareMicro`)}</p>
                </section>
            ) : null}
        </div>
    );
}
